import { Box } from '@material-ui/core';
import { formatMoney, LenderName, LoanStatusExternal, PaymentIntent } from '@nemuru-eng/js-api-client';
import { formatDistanceToNow } from 'date-fns';
import { ca, enUS, es } from 'date-fns/locale';

import { CheckoutSessionEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/entities/CheckoutSessionEntity';
import { useWidth } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useWidth';
import { useSetSelectedPaymentIntentMutation } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/PaymentIntentList/hooks/useSetSelectedPaymentIntentMutation';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { LenderIcon } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { IconByAllowedPaymentMethodType } from '@/components/shared/components/PricingsTable/constants/IconByAllowedPaymentMethodType';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { StatusTagType } from '@/config/types';
import { STYLE_PARAMS_BY_STATUS } from '@/constants/styleParamsByStatus';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export const DISABLED_STATUS = [LoanStatusExternal.DENIED, LoanStatusExternal.DESISTED, LoanStatusExternal.CANCELLED];

const formatDateFromNow = (date: Date, options?: { language: string }) => {
  options = options || { language: 'es' };
  const { language } = options;
  const locale = language === 'es' ? es : language === 'ca' ? ca : enUS;
  return formatDistanceToNow(date, {
    addSuffix: true,
    locale,
  });
};

interface PaymentIntentCardProps {
  paymentIntent: PaymentIntent;
  isSelected: boolean;
  checkoutSessionEntity: CheckoutSessionEntity;
}

export const PaymentIntentCard = ({ paymentIntent, isSelected, checkoutSessionEntity }: PaymentIntentCardProps) => {
  const {
    state: { language },
  } = useConfigContext();

  const { ref, width } = useWidth<HTMLDivElement>();
  const { palette } = useCustomTheme();
  const { getTextIdTranslation: t } = useTranslation();

  const isSmall = width && width < 400;

  const setSelectedPaymentIntentMutation = useSetSelectedPaymentIntentMutation(paymentIntent, checkoutSessionEntity);

  const conditions = paymentIntent.conditions;

  if (!conditions) {
    return null;
  }

  const isDisabled = DISABLED_STATUS.includes(paymentIntent.status);

  const onSelect = async () => {
    if (isDisabled) {
      return;
    }
    await setSelectedPaymentIntentMutation.mutateAsync();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <CustomPaper
      ref={ref}
      fill="paper"
      style={{
        padding: '16px',
        ...(isSelected && { boxShadow: `0px 0px 0px 2px ${palette.primary.main}` }),
      }}
    >
      <FlexRow gridGap={16}>
        {!isSmall && (
          <CustomPaper
            fill="grey.light"
            boxProps={{
              width: 'fit-content',
              flexShrink: 0,
            }}
            style={{
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {paymentIntent.providerName ? (
              <LenderIcon size={16} lenderName={paymentIntent.providerName as LenderName} />
            ) : (
              <Icon size={16}>{IconByAllowedPaymentMethodType(paymentIntent.paymentMethodType)}</Icon>
            )}
          </CustomPaper>
        )}
        <Box flexGrow={1} minWidth={0} display="flex" flexDirection="column">
          <Font variant="body2" color="textPrimary" className="overflow-text-ellipsis">
            <strong>
              {formatMoney({ value: conditions.userDefinedQuota, currency: conditions.currency })}
              {`${t('PER_MONTH')}`}
              {` ${t('IN')} `}
              {conditions.term ?? '-'} {t('MONTHS')}
            </strong>
          </Font>
          <Font variant="caption" color="textSecondary" className="overflow-text-ellipsis">
            <Text tId="CREATED_AT" /> {formatDateFromNow(paymentIntent.createdAt, { language })}
          </Font>
        </Box>
        {!isSelected && !isSmall && !isDisabled && (
          <StandardButton
            color="default"
            variant="text"
            size="small"
            loading={setSelectedPaymentIntentMutation.isLoading}
            onClick={onSelect}
          >
            {t('SELECT')}
          </StandardButton>
        )}
        <Box minWidth={80} display="flex" justifyContent="flex-end">
          <StatusTag
            type={STYLE_PARAMS_BY_STATUS[paymentIntent.status]?.colorPaletteName ?? StatusTagType.DEFAULT}
            colorType={STYLE_PARAMS_BY_STATUS[paymentIntent.status]?.colorType}
            labelTextId={paymentIntent.status}
          />
        </Box>
      </FlexRow>
      {!isSelected && isSmall && !isDisabled && (
        <Box mt={2}>
          <StandardButton
            color="default"
            variant="outlined"
            size="small"
            loading={setSelectedPaymentIntentMutation.isLoading}
            onClick={onSelect}
            fullWidth
          >
            {t('SELECT')}
          </StandardButton>
        </Box>
      )}
    </CustomPaper>
  );
};
